import type { FeedbackItem, TriageStatus } from './api.ts'

/** Only identifiers and filters travel in links; feedback text stays in the page. */
export interface FeedbackReviewFilters {
  readonly status: TriageStatus
  readonly account?: string | undefined
  readonly from?: string | undefined
  readonly to?: string | undefined
}

export interface FeedbackSequenceItem {
  readonly id: string
  readonly verificationId: string
  readonly accountId: string
  readonly at: string
}

export interface FeedbackSequence {
  readonly next: FeedbackSequenceItem | null
  readonly horizon: string
}

const STATUSES: readonly TriageStatus[] = ['pending', 'agree', 'disagree', 'all']

export function feedbackFilters(query: URLSearchParams): FeedbackReviewFilters | undefined {
  if (!['status', 'account', 'from', 'to'].some((key) => query.has(key))) return undefined
  const status = STATUSES.find((value) => value === query.get('status')) ?? 'pending'
  const value = (key: string) => query.get(key) || undefined
  return {
    status,
    account: value('account'),
    from: value('from'),
    to: value('to'),
  }
}

export function feedbackQuery(filters: FeedbackReviewFilters | undefined): URLSearchParams {
  const query = new URLSearchParams()
  if (filters === undefined) return query
  query.set('status', filters.status)
  for (const key of ['account', 'from', 'to'] as const) {
    const value = filters[key]
    if (value !== undefined && value !== '') query.set(key, value)
  }
  return query
}

/** The cursor is the event's immutable reception order, never a shifting page offset. */
export function feedbackCursor(item: Pick<FeedbackItem, 'id' | 'at'>) {
  return { afterAt: item.at, afterId: item.id }
}

/** Await the acknowledged judgement before moving; failure leaves the draft with its event. */
export async function saveFeedbackAndAdvance(
  save: () => Promise<unknown>,
  advance: () => Promise<void>,
  onSaved: () => void,
): Promise<void> {
  await save()
  onSaved()
  await advance()
}
